import { BaseController } from '../../common/base/controller';
import { adminStateName } from '../admin/admin.state';

export class LoginController extends BaseController {
  constructor(
    controllerService,
    User,
    fdFormFieldsService,
    $scope,
    cfpLoadingBar,
    loginFormPartial,
    featureFlagService,
  ) {
    'ngInject';
    super({
      controllerService,
      User,
      fdFormFieldsService,
      $scope,
      cfpLoadingBar,
      loginFormPartial,
      featureFlagService,
    });

    this.credentials = {};
  }

  $onInit() {
    this.checkSSO();
    this.configureFields();
  }

  checkSSO() {
    const { jwt, accessToken, error } = this.$stateParams;

    this.isSSO = !!this.appConfig.ssoEnabled;

    if (accessToken) {
      this.isSSO = true;
      this.credentials = { username: 'finoSSO', password: accessToken };
      this.doAuthenticate();
    }

    if (jwt) {
      // this token is already the final authenticated token, only need to inject into the application
      this.authService.injectToken(jwt);
      if (this.authService.isAuthenticated()) {
        this.returnToOriginalState();
      }
      return;
    }

    if (error) {
      this.isSSO = true;
      this.errorMessage = `sso.login.error.${error}`;
      return;
    }

    if (!accessToken) {
      this.redirectToHostedLogin();
    }
  }

  /**
   * With the Keycloak hosted login, the login form is rendered by Keycloak (kwh theme), this page only forwards.
   * The form stays hidden while we ask the backend, so it does not flash before the redirect.
   */
  redirectToHostedLogin() {
    this.hostedLoginPending = true;
    return this.authService.isHostedLogin().then((hostedLogin) => {
      if (hostedLogin) {
        this.$window.location.replace('/api/v0/auth/oidc/init');
        return;
      }
      this.hostedLoginPending = false;
    });
  }

  configureFields() {
    const { $scope } = this;

    // Handle branchId (for santander only!)
    if (this.isSantander) {
      // Show branchId input
      this.withBranchId = true;
      this.branchIdOptions = { pattern: '\\d{4}' };

      // BranchId is only required for "fo"-users
      $scope.$watch('vm.credentials.username', () =>
        this.checkBranchIdRequired(),
      );
    }
  }

  loginViaSSO() {
    this.$window.location.href = '/api/v0/auth/oidc/init';
  }

  checkBranchIdRequired() {
    const { username } = this.credentials;
    this.isBranchIdRequired = username === 'santander-fo@santander.de';
  }

  submit() {
    this.checkBranchIdRequired();
    this.fdFormFieldsService
      .validate(this.loginForm)
      .then(() => this.doAuthenticate());
  }

  doAuthenticate() {
    const { authService, credentials, cfpLoadingBar } = this;

    return (
      authService
        .authenticate(credentials)
        .busy(this, 'isSubmitBusy')
        .then(this.returnToOriginalState.bind(this))
        .catch(this.handleError.bind(this))
        // Fix to prevent loading bar from endless loading after failing/abortion
        .finally(() => cfpLoadingBar.complete())
    );
  }

  cancelAuthenticate() {
    this.authService.abortAuthenticate();
  }

  returnToOriginalState() {
    const { $state, returnTo } = this;

    // Check user permission specific redirect rules.
    this.maybeSetReturnState(returnTo);

    $state.go(returnTo.state, returnTo.params, { reload: true });
  }

  maybeSetReturnState(returnTo) {
    // If the user is an admin with administer users permission, redirect to the admin page
    let isAdminWithAdministerUsersPermission =
      this.userService.isAdmin() &&
      this.userService.hasPermission(
        this.userService.Permissions.Admin.administerUsers,
      );
    if (isAdminWithAdministerUsersPermission) {
      returnTo.state = adminStateName;
    }
  }

  handleError(response) {
    switch (response.status) {
      case 401: {
        this.errorMessage = 'login.error.401.unauthorized';
        break;
      }
      case 403: {
        this.errorMessage = 'login.error.403.forbidden';
        break;
      }
      case 412: {
        this.errorMessage = 'login.error.412.password.reset';
        break;
      }
      case 423: {
        this.errorMessage = 'login.error.423.locked';
        break;
      }
      case 500:
      default: {
        this.errorMessage = 'login.error.unknown';
        break;
      }
    }
  }
}
